import AppleLogo from 'assets/icons/AppleLogo';
import WindowsLogo from 'assets/icons/WindowsLogo';
import { BaseButtonMd } from 'components/ui/BaseButton';
import { DOWNLOAD_LINK } from 'constants/routes';
import { cloneElement, useCallback } from 'react';
import { t } from 'services/i18n.service';
import { useAppSelectorShallow } from 'store/hooks';
import { platformSelector } from 'store/reducer/setting/selectors';

interface Props {
  onRetry: () => void;
}

interface DownloadOptionProps {
  platformOptions: { icon: JSX.Element; link: string; label: string };
  isActive?: boolean;
}

const platforms = [
  {
    label: 'Windows',
    link: DOWNLOAD_LINK.WINDOWS_APP,
    icon: <WindowsLogo styles='~w-4/6 h-auto' />
  },
  {
    label: 'Mac',
    link: DOWNLOAD_LINK.MAC_APP,
    icon: <AppleLogo styles='~w-4/6 h-auto' />
  }
];

const DownloadOption = ({ platformOptions, isActive }: DownloadOptionProps) => {
  const { label, link, icon } = platformOptions;

  const handleOnClick = useCallback(() => {
    window.open(link, '_blank');
  }, [link]);

  return (
    <div
      className={`w-11/12 md:w-fit flex flex-col items-center ~gap-1.5/2 ~px-3/6 ~py-6/3 hover-scale ${isActive && '~mb-2/12'}`}>
      <a
        target='_blank'
        href={link}
        rel='noreferrer'
        className={`~px-3/4 ~py-2/3 ${isActive ? 'bg-primary-500 rounded-2xl shadow-lg' : '~rounded-md/xl shadow-md bg-primary-400'}`}
        onClick={(event) => {
          event.preventDefault();

          handleOnClick();
        }}>
        {cloneElement(icon, {
          styles: isActive ? '~w-10/24 h-auto' : '~w-4/9 h-auto'
        })}
      </a>
      <p className={`~text-xs/lg ${isActive && '~mt-1.5/3'}`}>{t(label)}</p>
    </div>
  );
};

const DesktopRedirectFallbackView = ({ onRetry }: Props) => {
  const { platform: currentPlatform } = useAppSelectorShallow(platformSelector);

  return (
    <div className='flex flex-col items-center justify-center ~gap-8/12'>
      <h2 className='text-center text-primary-900'>
        {t('auth_page.desktop_app_not_found_opening_web')}
      </h2>
      <div className='flex items-center ~gap-8/10'>
        <BaseButtonMd
          onClick={() => onRetry()}
          title={t('auth_page.try_again')}
          additionalStyles='min-w-44 ~px-4/8'
        />
      </div>

      <div className='flex flex-col items-center ~mt-4/6 ~gap-3/4'>
        <p className='text-center ~text-sm/lg text-primary-900'>
          {t('onboard_flow.desktop_download_cta')}
        </p>
        <div className='flex flex-wrap items-center justify-center ~gap-3/4'>
          {platforms.map((platform) => (
            <DownloadOption
              key={platform.label}
              platformOptions={platform}
              isActive={currentPlatform === platform.label}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default DesktopRedirectFallbackView;
